import { apiSlice } from "../stores/base/api-slice";
import { PaginatedList } from "../model/common/paginated-list";
import { ProviderCustomerOrder } from "../model/customer/customer";
import { GridSolarProviderModel, ProviderDashboardData } from "../model/provider/provider-dashboard-data";
import moment from "moment";
import { OrderWithReducedDetails } from "../model/orders/orders";
import { ConfirmNewMemberInvitationRequest, NewSolarProviderRequest, PermissionResponse } from "./contract";
import { ListResponseModel, toQueryString } from ".";

const serviceBaseurl = "/user/provider"
const solarProviderEndpoints = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get all for grid
    getAllForGrid: builder.query<ListResponseModel<Array<GridSolarProviderModel>>, any>({
      query: (params) => ({
        url: `/solar-provider/page${toQueryString(params)}`,
        method: "GET",
      }),
    }),

    getProvidersByCountry: builder.query<Array<{name: string, id: string}>, string>({
      query: (country) => ({
        url: `/product/provider/${country}`,
        method: "GET",
      }),
    }),

    // Get dashboard data
    getDashboardData: builder.query<ProviderDashboardData, { startDate: Date; endDate: Date }>({
      query: ({ startDate, endDate }) => {
        const start = moment(startDate).format();
        const end = moment(endDate).format();
        return {
          url: "/solar-provider/dashboard",
          method: "GET",
          params: { startDate: start, endDate: end },
        };
      },
    }),

    // Get most recent orders
    getMostRecentOrders: builder.query<
      PaginatedList<ProviderCustomerOrder>,
      { page: number; pageSize: number; start: Date; end?: Date }
    >({
      query: ({ page, pageSize, start, end }) => ({
        url: "/solar-provider/dashboard/most-recent-orders",
        method: "GET",
        params: { start, end, page, pageSize },
      }),
      transformResponse: (response: unknown) => {
        const typedResponse = response as PaginatedList<OrderWithReducedDetails>;
        
        return {
          ...typedResponse,
          items: typedResponse.items.map((item) => {
            // Ensure all required ProviderCustomerOrder properties are present
            return {
              ...item,
              paymentDate: item.paymentDate ? new Date(item.paymentDate) : undefined,
              statusChanged: item.statusChanged ? new Date(item.statusChanged) : undefined,
              // Adding any missing properties that might be required by ProviderCustomerOrder
              customerPhoneNumber: item.customerPhoneNumber || "",
              productName: item.productName || "",
              customerAddress: item.customerAddress || "",
              amountPaidFor: item.amountPaidFor || 0,
              productQuantity: item.productQuantity || 0,
              totalAmount: item.totalAmount || 0,
            } as unknown as ProviderCustomerOrder;
          }),
        } as PaginatedList<ProviderCustomerOrder>;
      },
    }),

    // Get company first login
    getCompanyFirstLogin: builder.query<any, { companyId?: string }>({
      query: ({ companyId }) => ({
        url: "/product/company/login-status",
        method: "GET",
        params: { Id: companyId },
      }),
    }),

    // Fill company info
    fillCompanyInfo: builder.mutation<any, any>({
      query: (data) => ({
        url: "/product/fill-company-info",
        method: "POST",
        body: data,
      }),
    }),

    // Get all team members
    getAllTeamMembers: builder.query<any, { search?: string, companyId?: string }>({
      query: ({ search, companyId }) => ({
        url: `${serviceBaseurl}/get-all-team-members`,
        method: "GET",
        params: { search, companyId },
      }),
    }),

    // Remove team member
    removeTeamMember: builder.mutation<any, any>({
      query: (data) => ({
        url: `${serviceBaseurl}/remove-team-member`,
        method: "POST",
        body: data,
      }),
    }),

    // Toggle activate team member
    toggleActivateTeamMember: builder.mutation<any, any>({
      query: (data) => ({
        url: `${serviceBaseurl}/toggle-team-member-activity`,
        method: "POST",
        body: data,
      }),
    }),

    // Get all permissions
    getAllPermissions: builder.query<PermissionResponse, void>({
      query: () => ({
        url: `${serviceBaseurl}/get-all-permissions`,
        method: "GET",
      }),
    }),

    // Invite new team member
    inviteNewTeamMember: builder.mutation<any, any>({
      query: (data) => ({
        url: `${serviceBaseurl}/invite-new-team-member`,
        method: "POST",
        body: data,
      }),
    }),

    // Confirm new member invitation
    confirmNewMemberInvitation: builder.mutation<any, ConfirmNewMemberInvitationRequest>({
      query: (data) => ({
        url: `${serviceBaseurl}/confirm-new-team-member-invitation`,
        method: "POST",
        body: data,
      }),
    }),

    // Register new solar provider
    registerNewSolarProvider: builder.mutation<any, NewSolarProviderRequest>({
      query: (data) => ({
        url: `${serviceBaseurl}/new-provider`,
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetAllForGridQuery,
  useLazyGetAllForGridQuery,
  useGetProvidersByCountryQuery,
  useGetDashboardDataQuery,
  useLazyGetDashboardDataQuery,
  useGetMostRecentOrdersQuery,
  useLazyGetMostRecentOrdersQuery,
  useGetCompanyFirstLoginQuery,
  useLazyGetCompanyFirstLoginQuery,
  useFillCompanyInfoMutation,
  useGetAllTeamMembersQuery,
  useLazyGetAllTeamMembersQuery,
  useRemoveTeamMemberMutation,
  useToggleActivateTeamMemberMutation,
  useGetAllPermissionsQuery,
  useLazyGetAllPermissionsQuery,
  useInviteNewTeamMemberMutation,
  useConfirmNewMemberInvitationMutation,
  useRegisterNewSolarProviderMutation,
} = solarProviderEndpoints;